import { Edit } from "@mui/icons-material";
import { useRef } from "react";
import type { ProfilePictureProps } from "../types";
import { OptimizedSvg } from "@components/common/OptimizedSvg";
import { userProfileMain } from "@tvet-mis/psda-assets";
import { CircularProgress } from "@mui/material";
import { useAttachment } from "@hooks/useAttachment";

const SUPPORTED_IMAGE_FORMATS =
	"image/*,.jfif,.webp,image/jpeg,image/jpg,image/png,image/gif,image/bmp,image/webp,image/jfif";

const AVATAR_SIZE_PX = 192;

const ProfilePlaceholder: React.FC = () => (
	<OptimizedSvg
		src={userProfileMain}
		alt="user profile"
		className="user-profile-main"
		width={AVATAR_SIZE_PX}
		height={AVATAR_SIZE_PX}
		cacheKey="user-profile-main"
	/>
);

const ProfilePicture: React.FC<ProfilePictureProps> = ({ profilePicture, isUploading, onImageChange }) => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const { url: profilePictureUrl, isLoading: isLoadingPicture, error: profilePictureError } = useAttachment(
		profilePicture,
	);

	const showUserImage = Boolean(profilePicture && profilePictureUrl && !profilePictureError);

	const renderAvatarContent = () => {
		if (profilePicture) {
			if (isLoadingPicture) {
				return <CircularProgress size={40} />;
			}
			if (showUserImage) {
				return (
					<img
						src={profilePictureUrl}
						alt="Profile"
						className="h-full w-full rounded-full object-cover"
					/>
				);
			}
		}
		return <ProfilePlaceholder />;
	};

	return (
		<div
			className="relative mb-4 flex h-48 w-48 cursor-pointer items-center justify-center rounded-full group"
			onClick={() => {
				if (!isUploading) {
					fileInputRef.current?.click();
				}
			}}>
			<div className="flex h-48 w-48 items-center justify-center overflow-hidden rounded-full bg-gray-100">
				{renderAvatarContent()}
			</div>

			<input
				type="file"
				ref={fileInputRef}
				onChange={onImageChange}
				className="hidden"
				accept={SUPPORTED_IMAGE_FORMATS}
				disabled={isUploading}
			/>

			<div
				className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-black bg-opacity-0 transition-all duration-200 group-hover:bg-opacity-30"
				aria-hidden="true">
				<Edit className="h-6 w-6 text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
			</div>

			{isUploading && (
				<div className="absolute inset-0 flex items-center justify-center rounded-full bg-black bg-opacity-50">
					<CircularProgress size={28} sx={{ color: "#ffffff" }} />
				</div>
			)}
		</div>
	);
};

export default ProfilePicture;
